// src/components/sales/pages/clients/ClientsView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Users, Pencil, Trash2, X, Search } from "lucide-react";
import ClientFormModal from "./ClientFormModal";

export default function ClientsView({
  clients,
  dataBlocks,
  loading,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,

  message,
  onClearMessage,
}) {
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);

  // ✅ Debounced Search
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [searchScope, setSearchScope] = useState("all"); // all | company | contact | email | phone | dataBlock | status

  useEffect(() => {
    const t = setTimeout(() => setSearchApplied(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const headers = useMemo(
    () => ["Company", "Contact", "Email/Phone", "Status", "Data Block", "Actions"],
    []
  );

  const openCreate = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const safeId = (row) => {
    const v = row?.id ?? row?.clientId ?? row?.clientID ?? row?.client_id;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const filteredClients = useMemo(() => {
    const list = Array.isArray(clients) ? clients : [];
    const term = (searchApplied || "").trim().toLowerCase();
    if (!term) return list;

    const fullNameOf = (c) =>
      `${c?.designation ? `${c.designation} ` : ""}${c?.firstName || ""} ${c?.secondName || ""}`.trim();

    const idStrOf = (c) => String(c?.id ?? c?.clientId ?? c?.clientID ?? c?.client_id ?? "");

    const dataBlockOf = (c) => c?.dataBlock ?? c?.dataBlockName ?? c?.blockName ?? "";
    const statusOf = (c) => c?.clientStatus ?? c?.status ?? c?.client_state ?? "";

    const haystack = (c) => {
      const fullName = fullNameOf(c);
      const idStr = idStrOf(c);

      switch (searchScope) {
        case "company":
          return [c?.companyName, c?.industry, c?.companyType, idStr]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        case "contact":
          return [fullName, c?.position, idStr].filter(Boolean).join(" ").toLowerCase();

        case "email":
          return [c?.emailAddress, idStr].filter(Boolean).join(" ").toLowerCase();

        case "phone":
          return [c?.phoneNumber, idStr].filter(Boolean).join(" ").toLowerCase();

        case "dataBlock":
          return [dataBlockOf(c), idStr].filter(Boolean).join(" ").toLowerCase();

        case "status":
          return [statusOf(c), idStr].filter(Boolean).join(" ").toLowerCase();

        case "all":
        default:
          return [
            c?.companyName,
            c?.industry,
            c?.companyType,
            fullName,
            c?.position,
            c?.emailAddress,
            c?.phoneNumber,
            statusOf(c),
            dataBlockOf(c),
            idStr,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
      }
    };

    return list.filter((c) => haystack(c).includes(term));
  }, [clients, searchApplied, searchScope]);

  const totalCount = clients?.length || 0;
  const shownCount = filteredClients?.length || 0;

  const searching = (searchApplied || "").trim().length > 0;
  const typing = (searchInput || "").trim().length > 0;

  const clearSearch = () => {
    setSearchInput("");
    setSearchApplied("");
  };

  const scopeLabel =
    searchScope === "all"
      ? "All fields"
      : searchScope === "company"
      ? "Company"
      : searchScope === "contact"
      ? "Contact"
      : searchScope === "email"
      ? "Email"
      : searchScope === "phone"
      ? "Phone"
      : searchScope === "dataBlock"
      ? "Data Block"
      : "Status";

  const placeholder =
    searchScope === "company"
      ? "Search by company name ..."
      : searchScope === "contact"
      ? "Search by contact ..."
      : searchScope === "email"
      ? "Search by email address..."
      : searchScope === "phone"
      ? "Search by phone number..."
      : searchScope === "dataBlock"
      ? "Search by data block..."
      : searchScope === "status"
      ? "Search by status (e.g., Customer, Prospect, Lead)..."
      : "Search by company, contact, email, phone, status, or data block...";

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <Users className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Clients</h2>
            <p className="text-xs text-white/70">Manage your client directory</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            Add Client
          </button>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Message Banner */}
      {message?.text ? (
        <div
          className={[
            "mb-6 rounded-2xl px-4 py-3 text-sm border backdrop-blur-xl flex items-start justify-between gap-3",
            message.type === "error"
              ? "border-red-500/30 bg-red-500/10 text-red-200"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
          ].join(" ")}
        >
          <div className="whitespace-pre-wrap">{message.text}</div>
          {onClearMessage && (
            <button
              type="button"
              onClick={onClearMessage}
              className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : null}

      {/* Table Card */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_30px_rgba(255,255,255,0.06)]
 border border-white/20 overflow-hidden">
        {/* Top bar */}
<div className="p-6 bg-gradient-to-r from-white/5 via-white/10 to-white/5 backdrop-blur-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h3 className="text-lg font-bold">Clients List</h3>

            <div className="text-sm text-gray-200">
              {searching ? (
                <span>
                  Showing: <b>{shownCount}</b> of <b>{totalCount}</b>
                </span>
              ) : (
                <span>
                  Total: <b>{totalCount}</b>
                </span>
              )}
            </div>
          </div>

          {/* ✅ Search (Debounced + chips: Contact + Data Block + Status) */}
      {/* ✅ Search (Debounced + chips - calm theme) */}
<div className="mt-4">
  {/* Chips */}
  <div className="flex flex-wrap gap-2 justify-end">
    {[
      { key: "all", label: "All" },
      { key: "company", label: "Company" },
      { key: "contact", label: "Contact" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "dataBlock", label: "Data Block" },
      { key: "status", label: "Status" },
    ].map((chip) => {
      const active = searchScope === chip.key;
      return (
        <button
          key={chip.key}
          type="button"
          onClick={() => setSearchScope(chip.key)}
          className={[
            "px-3 py-1.5 rounded-xl text-xs border backdrop-blur-xl transition",
            active
              ? "bg-white/15 border-white/25 text-white"
              : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10",
          ].join(" ")}
        >
          {chip.label}
        </button>
      );
    })}
  </div>

  {/* Input */}
  <div className="mt-3 w-full">
    {/* بدل الـ gradient/glow القوي: إطار هادي */}
    <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl">
      <div className="relative rounded-2xl">
        <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/55" />

        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-2xl bg-transparent px-4 py-3 pr-11 text-right text-sm text-white placeholder:text-white/45 outline-none
                     focus:border-white/25 focus:ring-2 focus:ring-white/10"
        />

        {(typing || searching) && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center
                       w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition"
            title="Clear"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
        )}
      </div>
    </div>

    <div className="mt-2 text-xs text-white/55 text-right flex items-center justify-end gap-2">
      <span>
        Searching in: <span className="text-white/75 font-medium">{scopeLabel}</span>
      </span>
      {typing && searchInput !== searchApplied && (
        <span className="px-2 py-0.5 rounded-lg bg-white/5 border border-white/10 text-white/60">
          Applying...
        </span>
      )}
    </div>
  </div>
</div>

        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-white/10">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-right text-sm font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {(filteredClients || []).map((c) => {
                const id = safeId(c);

                const fullName = `${c.designation ? `${c.designation} ` : ""}${c.firstName || ""} ${
                  c.secondName || ""
                }`.trim();

                return (
                  <tr
                    key={id ?? `${c.emailAddress || "row"}-${c.phoneNumber || ""}`}
                    className="hover:bg-white/10 transition"
                  >
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold">{c.companyName || "-"}</div>
                      <div className="text-xs text-gray-300">
                        {c.industry || ""}
                        {c.industry && c.companyType ? " • " : ""}
                        {c.companyType || ""}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm">{fullName || "-"}</div>
                      <div className="text-xs text-gray-300">{c.position || "-"}</div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm">{c.emailAddress || "-"}</div>
                      <div className="text-xs text-gray-300">{c.phoneNumber || "-"}</div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                        {c.clientStatus || c.status || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 rounded text-xs bg-purple-500/15 text-purple-200 border border-white/15">
                        {c.dataBlock || c.dataBlockName || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => openEdit(c)}
                          className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                          disabled={loading}
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>

                        <button
                          onClick={() => {
                            if (id == null) return;
                            onDelete?.(id);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                          disabled={loading || id == null}
                          title={id == null ? "Invalid client id" : "Delete"}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && shownCount === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    {searching ? "No results match your search." : "No clients yet."}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openModal && (
        <ClientFormModal
          dataBlocks={dataBlocks || []}
          initialData={editing}
          onClose={closeModal}
          onSubmit={async (payload) => {
            const editId = editing ? safeId(editing) : null;

            if (editId != null) {
              const ok = await onUpdate?.(Number(editId), payload);
              if (ok) closeModal();
            } else {
              const ok = await onCreate?.(payload);
              if (ok) closeModal();
            }
          }}
        />
      )}
    </div>
  );
}
